import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const ShiftsContext = createContext();

export const ShiftsProvider = ({ children }) => {
  const [shifts, setShifts] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
  });

  const handleSearchChange = (value) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      search: value,
      currentPage: 1, // Reset to first page when searching
    }));
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      handleSearchChange(e.target.value);
    }
  };

  const fetchShifts = useCallback(async () => {
    setShifts((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/ahr/shifts${queryString}`);
      if (response.success) {
        setShifts({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination.last_page,
        }));
      }
    } catch (error) {
      setShifts({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = shifts.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const Pagination = () => PaginationComponent;

  return (
    <ShiftsContext.Provider
      value={{
        shifts,
        filterOptions,
        handleSearch,
        handleSearchChange,
        refetch: fetchShifts,
        Pagination,
      }}
    >
      {children}
    </ShiftsContext.Provider>
  );
};

export const useShifts = () => {
  const context = useContext(ShiftsContext);
  if (!context) {
    throw new Error("useShifts must be used within ShiftsProvider");
  }

  const fetchOptions = async (inputValue) => {
    const response = await getRequest(`/ahr/shifts?search=${inputValue}`);
    if (response.success) {
      return response.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    }
    return [];
  };

  return { ...context, fetchOptions };
};